import * as yup from 'yup';

import { getReferenceData } from '../mapping';
import type { ResourceType } from '../types';

/**
 * Creates a reference schema for the internal reference.
 *
 * @param expectedResourceType - The resource type expected on the reference
 * @returns a yup validation schema
 */
const createInternalReferenceSchema = (expectedResourceType: ResourceType) => {
  const expectedReferenceType = `is${expectedResourceType}`;
  const errorMessage = `\${path} is not ${expectedResourceType} reference`;
  const schema = yup
    .string()
    .test(expectedReferenceType, errorMessage, async (reference) => {
      const referenceData = getReferenceData({ reference });
      if (referenceData?.resourceType !== expectedResourceType) {
        return false;
      }

      const { id } = referenceData;

      return yup.string().uuid().isValid(id);
    });

  return schema;
};

/**
 * Creates a reference schema for multiple references for the internal reference.
 *
 * @param expectedResourceType - A list of resource types expected on the reference
 * @returns a yup validation schema
 */
const createMultipleInternalReferenceSchema = (
  expectedResourceType: ResourceType[],
) => {
  const resourceList = expectedResourceType.join(', ');
  const expectedReferenceType = `is one of: ${resourceList}`;
  const errorMessage = `\${path} is not one of: ${resourceList}`;
  const schema = yup
    .string()
    .test(expectedReferenceType, errorMessage, async (reference) => {
      const referenceData = getReferenceData({ reference });

      const hasData = referenceData?.resourceType && referenceData?.id;
      if (!hasData) {
        return false;
      }

      const { id, resourceType } = referenceData;

      const isValidType = expectedResourceType.includes(resourceType);
      if (!isValidType) {
        return false;
      }

      return yup.string().uuid().isValid(id);
    });

  return schema;
};

/**
 * Creates a schema for multiple references for the expected resource type.
 *
 * @param expectedResourceType - A list of resource types expected on the reference
 * @returns a yup validation schema
 */
export const createMultipleReferenceValidationSchema = (
  expectedResourceType: ResourceType[],
) => {
  const referenceSchema =
    createMultipleInternalReferenceSchema(expectedResourceType);
  const schema = yup
    .object({
      reference: referenceSchema,
    })
    .required();

  return schema;
};

/**
 * Creates a reference schema for the expected resource type.
 *
 * @param expectedResourceType - The resource type expected on the reference
 * @returns a yup validation schema
 */
export const createReferenceValidationSchema = (
  expectedResourceType: ResourceType,
) => {
  const referenceSchema = createInternalReferenceSchema(expectedResourceType);
  const schema = yup
    .object({
      reference: referenceSchema,
    })
    .required();

  return schema;
};
